import {
  Button,
  DropdownMenu,
  Input,
  Label,
  TableItem,
  TableMessage,
} from 'components/atoms';
import { FormInput } from 'components/molecules';
import LabeledDeliveryStatus from 'components/molecules/labeledStatus/LabeledDeliveryStatus';
import Popup from 'components/molecules/popup';
import DeliveryDiscrepancyPopup from 'components/molecules/popup/delivery-discrepancy/DeliveryDiscrepancyPopup';
import PopupTimelineForm from 'components/molecules/popup/timeline/PopupTimelineForm';
import TimeRangeSelector from 'components/molecules/timeRangeSelector/TimeRangeSelector';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useFormikContext } from 'formik';
import { useComponentVisible } from 'hooks';
import { IFormikContextType } from 'hooks/useFormikField';
import { useCustomParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import i18next from 'i18next';
import middleware from 'middleware';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  PiCaretLeft,
  PiClock,
  PiDotsThreeVerticalBold,
  PiGlobeSimple,
  PiPlus,
  PiTrash,
  PiXCircle,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useCancelDigitalDelivery,
  useGetDigitalDelivery,
  usePostDigitalDelivery,
  usePutDigitalDelivery,
  useWithdrawnDigitalDelivery,
} from 'services/digital-delivery';
import {
  DELIVERY_METHOD,
  IDigitalDeliveryBase,
  IDigitalDeliveryMutation,
  TRANSFER_METHOD,
} from 'services/digital-delivery/types';
import { useGetCountries } from 'services/enums';
import { useChangeDigitalDeliveryStatus } from 'services/music-product-digital/delivery';
import {
  DELIVERY_STATUS,
  SUBSCRIPTION_TYPE,
} from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { formatDateToInput } from 'utils/global/date.utils';
import { requiredEnumSelect } from 'utils/global/validation.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { array, boolean, object, string } from 'yup';

const digitalDeliveryInitialValues = (
  publishDate?: string
): IDigitalDeliveryBase => ({
  asap: false,
  isTimeZoneBased: false,
  countryCode: '',
  publishDate: publishDate ?? formatDateToInput(new Date()),
  publishTime: '00:00',
  subscriptionTypeId: SUBSCRIPTION_TYPE.all,
  endDate: null,
  endTime: null,
});

const digitalDeliveryBaseValidator = object().shape({
  asap: boolean().required(i18next.t('formik-error.required')),
  isTimeZoneBased: boolean().required(i18next.t('formik-error.required')),
  countryCode: string().required(i18next.t('formik-error.required')),
  publishDate: string().when('asap', {
    is: (asap: boolean) => !asap,
    then: schema => schema.required(i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable().optional(),
  }),
  publishTime: string().when('asap', {
    is: (asap: boolean) => !asap,
    then: schema => schema.required(i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable().optional(),
  }),
  subscriptionTypeId: requiredEnumSelect(SUBSCRIPTION_TYPE),
});

export const digitalDeliveryValidator = object().shape({
  included: array().of(digitalDeliveryBaseValidator),
  excluded: array().of(digitalDeliveryBaseValidator),
});

const formatTimeToMMSS = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const timeToSeconds = (timeString: string): number => {
  const [hours, minutes, seconds] = timeString.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
};

const MusicProductDigitalDeliveryDetail = () => {
  usePermissionGuard('DigitalDeliveryPermissionsLIST**');

  const productId = useCustomParamId('id', '/music-product-digital');
  const salesChannelId = useCustomParamId(
    'salesChannelId',
    '/music-product-digital'
  );
  const providerId = useCustomParamId('providerId', '/music-product-digital');

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { getSelectOptions } = useEnumToSelectOptions();

  const {
    values,
    setFieldValue,
    validateField,
    errors,
  }: IFormikContextType<IMusicProductDigitalForm> =
    useFormikContext<IMusicProductDigitalForm>();

  const digitalDelivery = useGetDigitalDelivery(
    productId,
    salesChannelId,
    providerId
  );

  const minPublishDate =
    new Date(values.releaseDate).getTime() > new Date().getTime()
      ? formatDateToInput(values.releaseDate)
      : formatDateToInput(new Date());

  const createDigitalDelivery = usePostDigitalDelivery();
  const updateDigitalDelivery = usePutDigitalDelivery();

  const updateDigitalDeliveryStatus = useChangeDigitalDeliveryStatus();

  const cancelDigitalDelivery = useCancelDigitalDelivery();
  const withdrawnDigitalDelivery = useWithdrawnDigitalDelivery();

  const { data: countries } = useGetCountries();

  const [previewRange, setPreviewRange] = useState({ start: 0, end: 0 });
  const previewRangeRef = useRef(previewRange);

  const getTrackDuration = useCallback(() => {
    if (
      values.tracks &&
      values.tracks.length > 0 &&
      values.tracks[0].tracks.length > 0
    ) {
      const audio = new Audio(values.tracks[0].tracks[0].mp3PreviewLink);
      return new Promise<number>(resolve => {
        audio.addEventListener('loadedmetadata', () => {
          resolve(Math.floor(audio.duration));
        });
        audio.addEventListener('error', () => {
          resolve(190);
        });
      });
    }
    return Promise.resolve(190);
  }, [values.tracks]);

  const [trackDuration, setTrackDuration] = useState(190);

  useEffect(() => {
    getTrackDuration().then(duration => {
      setTrackDuration(duration);
    });
  }, [getTrackDuration]);

  useEffect(() => {
    if (
      values.delivery?.expectTimestamp &&
      (previewRange.start > trackDuration || previewRange.end > trackDuration)
    ) {
      const defaultStart = 0;
      const defaultEnd = Math.min(60, trackDuration);
      setPreviewRange({ start: defaultStart, end: defaultEnd });
      previewRangeRef.current = { start: defaultStart, end: defaultEnd };
    }
  }, [
    trackDuration,
    values.delivery?.expectTimestamp,
    previewRange.start,
    previewRange.end,
  ]);

  const handleTimelineChange = useCallback((start: number, end: number) => {
    previewRangeRef.current = { start, end };
  }, []);

  const handleTimelineAdd = useCallback(
    (action: PopupActions) => {
      if (
        values.delivery?.expectTimestamp &&
        previewRangeRef.current.start === 0 &&
        previewRangeRef.current.end === 0
      ) {
        const defaultStart = 0;
        const defaultEnd = Math.min(60, trackDuration);
        setPreviewRange({ start: defaultStart, end: defaultEnd });
        previewRangeRef.current = { start: defaultStart, end: defaultEnd };
      } else {
        setPreviewRange(previewRangeRef.current);
      }
      action.close();
    },
    [values.delivery?.expectTimestamp, trackDuration]
  );

  const clearTimelineValues = useCallback(
    (action?: PopupActions) => {
      if (values.delivery?.expectTimestamp) {
        const defaultStart = 0;
        const defaultEnd = Math.min(60, trackDuration);
        setPreviewRange({ start: defaultStart, end: defaultEnd });
        previewRangeRef.current = { start: defaultStart, end: defaultEnd };
      } else {
        setPreviewRange({ start: 0, end: 0 });
        previewRangeRef.current = { start: 0, end: 0 };
      }
      action?.close();
    },
    [values.delivery?.expectTimestamp, trackDuration]
  );

  useEffect(() => {
    if (digitalDelivery.data && values.mainTitle.title) {
      setFieldValue(
        'delivery',
        middleware.digitalDelivery.queryToForm(digitalDelivery.data)
      );
      if (digitalDelivery.data.timestamp) {
        const start = timeToSeconds(digitalDelivery.data.timestamp.start);
        const end = timeToSeconds(digitalDelivery.data.timestamp.end);
        setPreviewRange({ start, end });
        previewRangeRef.current = { start, end };
      } else if (values.delivery?.expectTimestamp) {
        const defaultStart = 0;
        const defaultEnd = 30;
        setPreviewRange({ start: defaultStart, end: defaultEnd });
        previewRangeRef.current = { start: defaultStart, end: defaultEnd };
      }
    }
  }, [
    digitalDelivery.data,
    values.mainTitle.title,
    setFieldValue,
    values.delivery?.expectTimestamp,
  ]);

  const onSubmitDelivery = async (actions?: PopupActions | null) => {
    validateField('delivery');

    if (Object.keys(errors?.delivery ?? {}).length) {
      return toast.error(t('error-code.FORM_ERROR'));
    }

    if (values.delivery) {
      const deliveryIdentification = { productId, salesChannelId, providerId };

      const delivery: IDigitalDeliveryMutation = {
        ...deliveryIdentification,
        version: values.delivery.version,
        included: values.delivery.included.map(d => ({
          ...d,
          publishTime: `${d.publishTime}:00`.slice(0, 8),
        })),
        excluded: values.delivery.excluded.map(d => ({
          ...d,
          publishTime: `${d.publishTime}:00`.slice(0, 8),
        })),
        priority: false,
        timestamps:
          previewRange.start > 0 || previewRange.end > 0
            ? {
                start: formatTimeToMMSS(previewRange.start),
                end: formatTimeToMMSS(previewRange.end),
              }
            : null,
      };

      if (values.delivery.deliveryType !== DELIVERY_METHOD.Manual) {
        const isEmptyId =
          values.delivery.id === '00000000-0000-0000-0000-000000000000';

        if (isEmptyId) {
          return await createDigitalDelivery.mutateAsync(delivery, {
            onSuccess: async () => {
              await digitalDelivery.refetch();
              actions?.close();
            },
            onError: async error => onErrorShowAllErrorsToast(error),
          });
        }

        return await updateDigitalDelivery.mutateAsync(delivery, {
          onSuccess: async () => {
            await digitalDelivery.refetch();
            actions?.close();
          },
          onError: async error => onErrorShowAllErrorsToast(error),
        });
      }

      if (values.delivery.deliveryType === DELIVERY_METHOD.Manual) {
        return await updateDigitalDeliveryStatus.mutateAsync(
          {
            productId,
            providerId,
            salesChannel: salesChannelId,
            statusId: values.delivery.status,
          },
          {
            onSuccess: async () => await digitalDelivery.refetch(),
            onError: async error => onErrorShowAllErrorsToast(error),
          }
        );
      }
    }
  };

  const onCancelDelivery = async (actions: PopupActions) => {
    await cancelDigitalDelivery.mutateAsync(
      {
        productId,
        salesChannel: salesChannelId,
        providerId,
      },
      {
        onSuccess: async () => {
          await digitalDelivery.refetch();
          actions.close();
        },
        onError: async error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onWithdrawnDelivery = async (
    actions: PopupActions,
    priority: boolean
  ) => {
    await withdrawnDigitalDelivery.mutateAsync(
      {
        productId,
        salesChannelId,
        providerId,
        priority,
      },
      {
        onSuccess: async () => {
          await digitalDelivery.refetch();
          actions.close();
        },
        onError: async error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const getDeliveryNodes = (deliveries?: IDigitalDeliveryBase[]) => ({
    nodes: deliveries?.map((d, key) => ({ ...d, id: key })) ?? [],
  });

  const onAddDigitalDelivery = (prefix: 'included' | 'excluded') => {
    values.delivery &&
      setFieldValue(
        `delivery.${prefix}[${values.delivery[prefix].length}]`,
        digitalDeliveryInitialValues(minPublishDate)
      );
  };

  const onRemoveDigitalDelivery = (
    id: number,
    prefix: 'included' | 'excluded'
  ) => {
    values.delivery &&
      setFieldValue(
        `delivery.${prefix}`,
        values.delivery[prefix].filter((d, i) => i !== id)
      );
  };

  const isDeliveryMethodManual =
    values.delivery?.deliveryType === DELIVERY_METHOD.Manual;

  const isDeliveryCanceled =
    values.delivery?.status === DELIVERY_STATUS.canceled;

  const isDeliveryWithdrawn =
    values.delivery?.status === DELIVERY_STATUS.withdrawInProgress ||
    values.delivery?.status === DELIVERY_STATUS.withdrawn ||
    values.delivery?.status === DELIVERY_STATUS.withdrawError ||
    values.delivery?.status === DELIVERY_STATUS.withdrawWaiting;

  const cancelVisible = useComponentVisible<HTMLDivElement>(false);

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() =>
                navigate(`/music-product-digital/${productId}/delivery`)
              }>
              <Button.Icon icon={<PiCaretLeft />} />
            </Button>
          }
          title={
            values.delivery
              ? `${values.delivery.salesChannelName} - ${values.delivery.providerName}`
              : '-'
          }
          topbarContent={
            <div className='flex w-full items-center justify-end'>
              <div className='flex items-center gap-8 w-full justify-end font-medium'>
                {!isDeliveryCanceled &&
                  !isDeliveryWithdrawn &&
                  values.delivery?.expectTimestamp &&
                  values.releaseType === 1 && (
                    <PopupTimelineForm
                      header={t('timestamp.title')}
                      clearPopupValues={clearTimelineValues}
                      onAdd={handleTimelineAdd}
                      trigger={
                        <Button variant={BUTTON_VARIANT.default}>
                          <Button.Text text={t('button.timestamp')} />
                          <Button.Icon icon={<PiClock />} />
                        </Button>
                      }>
                      <div className='timeline-container'>
                        <TimeRangeSelector
                          totalDuration={trackDuration}
                          onChange={handleTimelineChange}
                          minDuration={5}
                          maxDuration={60}
                          name='previewRange'
                          audioUrl={
                            values.tracks?.[0]?.tracks?.[0]?.mp3PreviewLink
                          }
                          initialStart={previewRange.start}
                          initialEnd={previewRange.end}
                        />
                      </div>
                    </PopupTimelineForm>
                  )}
                {values.delivery &&
                  t(
                    `enum.DELIVERY_METHOD.${
                      DELIVERY_METHOD[values.delivery.deliveryType]
                    }`
                  )}
                {' - '}
                {values.delivery?.transferMethod !== undefined &&
                  t(
                    `enum.TRANSFER_METHOD.${Object.keys(TRANSFER_METHOD).find(
                      key =>
                        TRANSFER_METHOD[key as keyof typeof TRANSFER_METHOD] ===
                        values.delivery?.transferMethod
                    )}`
                  )}
                {values.delivery?.waitForAssetId && (
                  <Label color='blue' size='compact' withText>
                    {t('input.label.waitForAssetId')}
                  </Label>
                )}
                {values.delivery &&
                  (!isDeliveryMethodManual ? (
                    <LabeledDeliveryStatus status={values.delivery.status} />
                  ) : (
                    <div className='w-48'>
                      <FormInput.Select
                        name='delivery.status'
                        label={t('input.label.status')}
                        options={getSelectOptions(
                          DELIVERY_STATUS,
                          'enum.DELIVERY_STATUS'
                        )}
                        displayMode={false}
                      />
                    </div>
                  ))}
                <div className='flex gap-2'>
                  {values.delivery?.hasDiscrepancy && (
                    <DeliveryDiscrepancyPopup
                      name={`${values.delivery?.salesChannelName} ${values.delivery?.providerName}`}
                    />
                  )}
                  {values.delivery?.status === DELIVERY_STATUS.waiting && (
                    <Popup.CancelWarning
                      name={
                        values.delivery
                          ? `${values.delivery.salesChannelName} - ${values.delivery.providerName}`
                          : '-'
                      }
                      transI18nKey={'music-product-digital.delivery.warning'}
                      onCancel={onCancelDelivery}
                      trigger={
                        <Button
                          isClear
                          onlyIcon
                          variant={BUTTON_VARIANT.warning}>
                          <Button.Icon icon={<PiXCircle />} />
                        </Button>
                      }
                    />
                  )}
                  {!isDeliveryCanceled &&
                    !isDeliveryWithdrawn &&
                    isDeliveryMethodManual && (
                      <Button
                        variant={BUTTON_VARIANT.submit}
                        onClick={() => onSubmitDelivery()}
                        disabled={!values.delivery}>
                        <Button.Text text={t('button.change')} />
                        <Button.Icon icon={<PiGlobeSimple />} />
                      </Button>
                    )}

                  <Popup.CancelWarning
                    name={
                      values.delivery
                        ? `${values.delivery.salesChannelName} - ${values.delivery.providerName}`
                        : '-'
                    }
                    transI18nKey={
                      values.delivery?.status === DELIVERY_STATUS.notSend
                        ? 'music-product-digital.delivery.publish-warning'
                        : 'music-product-digital.delivery.change-warning'
                    }
                    onCancel={onSubmitDelivery}
                    trigger={
                      <Button
                        variant={BUTTON_VARIANT.submit}
                        disabled={!values.delivery}>
                        <Button.Text
                          text={
                            values.delivery?.status === DELIVERY_STATUS.notSend
                              ? t('button.publish')
                              : t('button.change')
                          }
                        />
                        <Button.Icon icon={<PiGlobeSimple />} />
                      </Button>
                    }
                  />
                  {!isDeliveryCanceled &&
                    !isDeliveryWithdrawn &&
                    !isDeliveryMethodManual &&
                    values.delivery?.status !== DELIVERY_STATUS.notSend && (
                      <>
                        <DropdownMenu
                          ref={cancelVisible.ref}
                          visible={cancelVisible.isComponentVisible}
                          style={{ padding: 5 }}
                          justifyPosition={'end'}>
                          <div className='flex flex-col w-full'>
                            <Popup.CancelWarning
                              name={
                                values.delivery
                                  ? `${values.delivery.salesChannelName} - ${values.delivery.providerName}`
                                  : '-'
                              }
                              transI18nKey={
                                'music-product-digital.delivery.withdrawn-warning'
                              }
                              onCancel={actions =>
                                onWithdrawnDelivery(actions, false)
                              }
                              trigger={
                                <Button isClear fullWidth>
                                  <Button.Text text={t('button.withdrawn')} />
                                </Button>
                              }
                            />
                            <Popup.CancelWarning
                              name={
                                values.delivery
                                  ? `${values.delivery.salesChannelName} - ${values.delivery.providerName}`
                                  : '-'
                              }
                              transI18nKey={
                                'music-product-digital.delivery.withdrawn-warning'
                              }
                              onCancel={actions =>
                                onWithdrawnDelivery(actions, true)
                              }
                              trigger={
                                <Button isClear fullWidth>
                                  <Button.Text
                                    text={t('button.withdrawn-priority')}
                                  />
                                </Button>
                              }
                            />
                          </div>
                        </DropdownMenu>
                        <Button
                          variant={BUTTON_VARIANT.default}
                          onlyIcon
                          isClear
                          disabled={!digitalDelivery.data}
                          onClick={cancelVisible.handleComponentVisible}>
                          <Button.Icon icon={<PiDotsThreeVerticalBold />} />
                        </Button>
                      </>
                    )}
                </div>
              </div>
            </div>
          }
        />
      }>
      {!isDeliveryMethodManual && (
        <div className='flex flex-col-reverse h-full w-full gap-4 justify-end '>
          <div className='flex w-full max-h-[45%] '>
            <Table
              id={'MusicProductDigitalDeliveryDetail2'}
              size='compact'
              data={getDeliveryNodes(values.delivery?.excluded)}
              columns={[
                {
                  label: t(`music-product-digital.delivery.excluded-country`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].countryCode`}
                        label={t('input.label.country')}
                        options={
                          countries
                            ? [
                                {
                                  label: t(
                                    'music-product-digital.delivery.all'
                                  ),
                                  value: 'ALL',
                                },
                                ...countries.map(country => ({
                                  label: t(`countries.${country.codeAlfa2}`),
                                  value: country.codeAlfa2,
                                })),
                              ]
                            : []
                        }
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.subscriptionType`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].subscriptionTypeId`}
                        label={t('input.label.subscriptionType')}
                        options={getSelectOptions(
                          SUBSCRIPTION_TYPE,
                          'enum.SUBSCRIPTION_TYPE'
                        )}
                        onChange={value => {
                          setFieldValue(
                            `delivery.excluded[${id}].subscriptionTypeId`,
                            value.value
                          );
                        }}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.asap`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].asap`}
                        label={t('input.label.asap')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.timeZoneBased`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.excluded[${id}].isTimeZoneBased`}
                        label={t('input.label.timeZoneBased')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.date`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <Input.ComplexWrapper
                        sizes={[60, 40]}
                        className='max-w-72'>
                        <FormInput.Text
                          name={`delivery.excluded[${id}].publishDate`}
                          type='date'
                          min={minPublishDate}
                          displayMode={false}
                          disabled={values.delivery?.excluded[id].asap}
                        />
                        <FormInput.Text
                          name={`delivery.excluded[${id}].publishTime`}
                          type='time'
                          displayMode={false}
                          disabled={values.delivery?.excluded[id].asap}
                        />
                      </Input.ComplexWrapper>
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: '',
                  renderCell: ({ id }) => {
                    return (
                      <TableItem justifyPosition='end'>
                        <Button
                          type='button'
                          variant={BUTTON_VARIANT.remove}
                          onlyIcon
                          onClick={() =>
                            onRemoveDigitalDelivery(id, 'excluded')
                          }
                          isClear>
                          <Button.Icon icon={<PiTrash />} />
                        </Button>
                      </TableItem>
                    );
                  },
                },
              ]}
              tableOptions={{
                renderAfterTable: () => (
                  <tfoot className='full-width-footer'>
                    <TableMessage
                      className='cursor-pointer'
                      onClick={() => onAddDigitalDelivery('excluded')}>
                      <TableItem.Text text={t('button.add')} />
                      <PiPlus />
                    </TableMessage>
                  </tfoot>
                ),
              }}
            />
          </div>
          <div className='flex w-full max-h-[45%]'>
            <Table
              id={'MusicProductDigitalDeliveryDetail2'}
              size='compact'
              data={getDeliveryNodes(values.delivery?.included)}
              columns={[
                {
                  label: t(`music-product-digital.delivery.included-country`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].countryCode`}
                        label={t('input.label.country')}
                        options={
                          countries
                            ? [
                                {
                                  label: t(
                                    'music-product-digital.delivery.all'
                                  ),
                                  value: 'ALL',
                                },
                                ...countries.map(country => ({
                                  label: t(`countries.${country.codeAlfa2}`),
                                  value: country.codeAlfa2,
                                })),
                              ]
                            : []
                        }
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.subscriptionType`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].subscriptionTypeId`}
                        label={t('input.label.subscriptionType')}
                        options={getSelectOptions(
                          SUBSCRIPTION_TYPE,
                          'enum.SUBSCRIPTION_TYPE'
                        )}
                        onChange={value => {
                          setFieldValue(
                            `delivery.included[${id}].subscriptionTypeId`,
                            value.value
                          );
                        }}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.asap`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].asap`}
                        label={t('input.label.asap')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.timeZoneBased`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <FormInput.Select
                        displayMode={false}
                        name={`delivery.included[${id}].isTimeZoneBased`}
                        label={t('input.label.timeZoneBased')}
                        options={[
                          { label: t('input.option.yes'), value: true },
                          { label: t('input.option.no'), value: false },
                        ]}
                      />
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: t(`input.label.date`),
                  renderCell: ({ id }) => (
                    <TableItem>
                      <Input.ComplexWrapper
                        sizes={[60, 40]}
                        className='max-w-72'>
                        <FormInput.Text
                          name={`delivery.included[${id}].publishDate`}
                          type='date'
                          min={minPublishDate}
                          displayMode={false}
                          disabled={values.delivery?.included[id].asap}
                        />
                        <FormInput.Text
                          name={`delivery.included[${id}].publishTime`}
                          type='time'
                          displayMode={false}
                          onChange={e => {
                            let value = e.target.value;
                            if (value && value.length === 5) {
                              value = `${value}:00`;
                            }
                            e.target.value = value;
                            e.persist();

                            const originalOnChange = e.target.onChange;
                            if (originalOnChange) originalOnChange(e);
                          }}
                          disabled={values.delivery?.included[id].asap}
                        />
                      </Input.ComplexWrapper>
                    </TableItem>
                  ),
                  resize: true,
                },
                {
                  label: '',
                  renderCell: ({ id }) => {
                    return (
                      <TableItem justifyPosition='end'>
                        <Button
                          type='button'
                          variant={BUTTON_VARIANT.remove}
                          onlyIcon
                          onClick={() =>
                            onRemoveDigitalDelivery(id, 'included')
                          }
                          isClear>
                          <Button.Icon icon={<PiTrash />} />
                        </Button>
                      </TableItem>
                    );
                  },
                },
              ]}
              tableOptions={{
                renderAfterTable: () => (
                  <tfoot className='full-width-footer'>
                    <TableMessage
                      className='cursor-pointer'
                      onClick={() => onAddDigitalDelivery('included')}>
                      <TableItem.Text text={t('button.add')} />
                      <PiPlus />
                    </TableMessage>
                  </tfoot>
                ),
              }}
            />
          </div>
        </div>
      )}
    </TopbarPage>
  );
};

export default MusicProductDigitalDeliveryDetail;
